import { isNotEmpty } from "@/fp";
import {
  ApplicationType,
  DataSourceType,
} from "@/views/customize/data-sources/data/model";

export interface CmsReport {
  id: string;
  small_img: string;
  a4_img: string;
  size: number;
}

export type ReportStatus = "pending" | "in-progress" | "completed" | "failed";

export type PdfReportSessionSection = {
  type: "session";
  session_id: number;
};

export type PdfReportTimeFrameSection = {
  type: "timeframe";
  time_type: "Week" | "Month" | "Year";
  time_value: string;
  /** Human-readable label for the period, e.g. "January 2026" or "Last 3 months" */
  displayName?: string;
  /** When true, aggregates (sums) data across multiple months for the same coordinate */
  aggregated?: boolean;
  aggregation_type?: "last_x_months" | "quarter" | "half_year" | "year";
  aggregation_value?: string;
  aggregation_months?: string[];
};

export type PdfReportTimeSection =
  | PdfReportSessionSection
  | PdfReportTimeFrameSection;

export type PdfReportSection = {
  section_id: string;
  question: string;
  label_id?: string;
  data_source: DataSourceType;
  application?: ApplicationType;
  time: PdfReportTimeSection;
};

export type PdfReportData = Record<string, any>;

export type PdfReport = {
  id: string;
  date: string;
  name: string;
  requested_by: string;
  template_id: string;
  status: ReportStatus;
  data: PdfReportData;
  sections: PdfReportSection[];
  reason?: string;
  is_scheduled: boolean;
  runtime: {
    data_sources: string[];
  };
};

export type ReportTemplateParameter =
  | "any_label"
  | "l0_label"
  | "application"
  | "severity"
  | "sensitivity";

export type ReportTemplateParameterValues = Partial<
  Record<ReportTemplateParameter, { id: string; name: string }>
>;

export type ReportTemplateGeneratorParameterValues = Partial<
  Record<ReportTemplateParameter, Array<{ id: string; name: string }>>
>;

export type ReportTemplateSection = {
  id: string;
  question: string;
  displayed_question: string;
  data_source: DataSourceType;
  application?: ApplicationType;
  parameters: ReportTemplateParameter[];
  parameters_values?: ReportTemplateParameterValues;
  runtime: {
    uid: string | number;
    small_img: string;
    a4_img: string;
  };
};

export type GeneratorReportTemplateSection = {
  id: string;
  question: string;
  displayed_question: string;
  data_source: DataSourceType;
  application?: ApplicationType;
  parameters: ReportTemplateParameter[];
  parameters_values?: ReportTemplateGeneratorParameterValues;
  runtime: {
    uid: string | number;
    small_img: string;
    a4_img: string;
  };
};

export type ReportScheduleOption = "weekly" | "monthly" | "none";

export type ReportTemplateAccess = "private" | "public";

export type ReportTemplate = {
  id: string;
  org_id?: string;
  access: ReportTemplateAccess;
  name: string;
  author: string;
  last_modified_by: string;
  sections: ReportTemplateSection[];
  created_at: string;
  updated_at: string;
  scheduled: ReportScheduleOption;
  mailing_list: string[];
};

export type CreateReportTemplateInput = Pick<
  ReportTemplate,
  "sections" | "name" | "scheduled" | "mailing_list" | "access"
>;

export type GenerateReportTemplateSection = {
  id: string;
  question: string;
  data_source: DataSourceType;
  application?: ApplicationType;
  parameters: ReportTemplateParameter[];
  parameters_values?: Record<
    ReportTemplateParameter,
    { id: string; name: string }
  >;
  time: PdfReportTimeSection;
  /** Groups the aggregated + per-month pages for one question under a single TOC entry */
  toc_group?: string;
  /** True = this is the aggregated summary page (TOC points here); False = individual month sub-page */
  toc_group_is_summary?: boolean;
};

function group_sections_by_id(
  sections: ReportTemplateSection[]
): GeneratorReportTemplateSection[] {
  const sectionMap: { [key: string]: GeneratorReportTemplateSection } = {};

  sections.forEach((section) => {
    if (!sectionMap[section.id]) {
      // Initialize a new section with empty parameter values
      sectionMap[section.id] = {
        ...section,
        parameters_values: {} as ReportTemplateGeneratorParameterValues,
      };
    }

    const existingSection = sectionMap[section.id];

    // Merge parameter values
    if (section.parameters_values) {
      Object.entries(section.parameters_values).forEach(([param, value]) => {
        if (
          !existingSection.parameters_values![param as ReportTemplateParameter]
        ) {
          existingSection.parameters_values![
            param as ReportTemplateParameter
          ] = [];
        }
        existingSection.parameters_values![
          param as ReportTemplateParameter
          // @ts-ignore
        ]!.push(value);
      });
    }
  });

  return Object.values(sectionMap);
}

function spread_sections(
  groupedSections: GeneratorReportTemplateSection[]
): ReportTemplateSection[] {
  const result: ReportTemplateSection[] = [];

  groupedSections.forEach((section) => {
    if (isNotEmpty(section.parameters_values)) {
      const parameterEntries = Object.entries(section.parameters_values!) as [
        ReportTemplateParameter,
        { id: string; name: string }[]
      ][];

      parameterEntries.forEach(([param, values]) => {
        values.forEach((value) => {
          const newSection: ReportTemplateSection = {
            ...section,
            parameters_values: {
              [param]: value,
            },
          };
          result.push(newSection);
        });
      });
    } else {
      // @ts-ignore
      result.push(section);
    }
  });
  return result;
}

export const TemplateGeneratorHelper = {
  group_sections_by_id,
  spread_sections,
};
